import React, { useState } from "react";
import { Link } from "react-router-dom";
import loginPageImg from "../LoginPage/saudi-man-login-page.webp";
import MashoorahLogo from "../LandingPage/Logo/mashoorah-logo-black.svg";
import create from "zustand";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { emailRegex } from "../../../lib/constants";
import { useLogin } from "../../../features/Auth/authQueries";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { BaseDialog, Box, Button, Checkbox, Input, Stack, Typography } from "knack-ui";
import logo from "../../../components/Asset/mashoorah-logo-art-dark.svg";
import SVG from "../../../components/SVG/SVG";
import OTPSection from "../../../pages/Auth/Login/OTPSection";
import AccountLockDialog from "../../../features/Auth/ui/AccountLockDialog";

const colorOrange = "#ed7f3d";

interface LoginForm {
  email: string;
  password?: string;
  tos: boolean;
}

interface LoginStoreProps {
  email: string;
  setEmail: (email: string) => void;
  password?: string;
  setPassword: (password: string) => void;
}


export const LoginStore = create<LoginStoreProps>((set) => ({
  email: "",
  setEmail: (email: string) => set((state) => ({ ...state, email })),
  password: "",
  setPassword: (password: string) => set((state) => ({ ...state, password }))
}));

export const LoginPagesTransition: Variants = {
  hidden: { y: -20, opacity: 0 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  },
  exit: {
    y: 200,
    opacity: 0,
    transition: {
      ease: "easeInOut",
      duration: 0.3
    }
  }
};


const LoginPage = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmailState] = useState("");
  const [password, setPasswordState] = useState("");
  const { setEmail, setPassword } = LoginStore();
  const { mutateAsync: onLogin, isLoading } = useLogin();
  const {
    register,
    handleSubmit,
    control,
    setError,
    watch,
    formState: { errors }
  } = useForm<LoginForm>({ defaultValues: { tos: true } });
  const [showPwd, showHidePwd] = useState<boolean>(false);

  const onSubmit: SubmitHandler<LoginForm> = async (data) => {
    try {
      setErrorMessage("");
      const res = await onLogin({
        email: data.email,
        password: data.password as string
      });
      if (res.message === "Password is required") {
        showHidePwd(true);
      }
      if (res.message === "success") {
        setShowOtp(true);
        setEmail(data.email);
        setPassword(data?.password as string);
        setEmailState(data.email);
        setPasswordState(data?.password as string);
      }
    } catch (error: any) {
      if (error.message === "Incorrect credentials provided") {
        setError("email", { type: "validate" });
        setError("password", { type: "validate" });
        setErrorMessage(error.message);
        if (error.blocked) {
          setErrorMessage("Account is temporarily blocked");
          setShowBlockedModal(true);
          return;
        }
        if (error.attempts > 2) {
          setErrorMessage(`You have ${5 - error.attempts} attempts left before your account gets temporarily locked`);
        }
      } else if (error.message === "Account is temporarily blocked") {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(error.message);
      }
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="flex h-screen">
      <AnimatePresence exitBeforeEnter>
        {!showPwd && !showOtp && (
          <div className="flex w-full h-full" key="login">
            <div className="flex-1 flex items-center justify-center bg-gray-100 flex-col relative">
              <h1 
                className="text-left w-64 md:w-96 text-4xl mb-16" 
                style={{ fontFamily: "AvenirNext", fontStyle: "normal", fontWeight: 500 }}
              >
                Login
              </h1>
              <motion.form 
                className="flex flex-col w-64 md:w-96"
                onSubmit={handleSubmit(onSubmit)}
                variants={LoginPagesTransition}
                exit="exit"
                initial="hidden"
                animate="visible"
              >
                <label className="ml-1 mb-2 mt-2" htmlFor="email">
                  Email address
                </label>
                <input
                  {...register("email", {
                    required: "Required field",
                    pattern: {
                      value: emailRegex,
                      message: "Please provide a valid email address"
                    }
                  })}
                  type="text"
                  id="email"
                  className="w-full p-3 border rounded-lg bg-transparent focus:outline-none"
                  style={{ borderColor: errors.email ? "#ef4444" : "#ed7f3d" }}
                />
                {errors.email && (
                  <span className="text-red-500 text-sm mt-1 ml-1">{errors.email.message}</span>
                )}
                {errorMessage && !errors.email && (
                  <Typography
                    as={Stack}
                    alignItems="center"
                    gap={2}
                    fontWeight="semibold"
                    justifyContent="center"
                    variant="body2"
                    className="mt-4"
                    color="warning"
                  >
                    <SVG name="Danger" />
                    <span>{errorMessage}</span>
                  </Typography>
                )}
                <Link to="/auth/forgot-password" className="flex items-start cursor-pointer ml-1 italic mt-2">
                  Forgot password?
                </Link>
                <button
                  type="submit"
                  disabled={isLoading || !watch("tos")}
                  className="py-3 font-semibold mt-4 md:mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: colorOrange,
                    width: "fit-content",
                    color: "white",
                    padding: "0.6rem 2rem",
                    alignSelf: "end",
                    borderRadius: "9999px",
                    transition: "background-color 0.3s ease",
                    fontWeight: 600
                  }}
                >
                  {isLoading ? "Loading..." : "Continue"}
                </button>
              </motion.form>
              <Link to="/" className="flex items-start w-64 md:w-96 mt-20">
                <img src={MashoorahLogo} alt="Mashoorah Logo" className="h-16 md:h-20 w-auto" />
              </Link>
            </div>

            <div 
              className="flex-1 bg-cover bg-center hidden lg:block"
              style={{ backgroundImage: `url(${loginPageImg})` }}
            />
          </div>
        )}
        {!showOtp && showPwd && (
          <div className="flex w-full h-full" key="pwd">
            <div className="flex-1 flex items-center justify-center bg-gray-100 flex-col relative">
              <h1 
                className="text-left w-64 md:w-96 text-4xl mb-16" 
                style={{ fontFamily: "AvenirNext", fontStyle: "normal", fontWeight: 500 }}
              >
                Login
              </h1>
              <motion.form 
                className="flex flex-col w-64 md:w-96"
                onSubmit={handleSubmit(onSubmit)}
                variants={LoginPagesTransition}
                exit="exit"
                initial="hidden"
                animate="visible"
              >
                <label className="ml-1 mb-2 mt-2" htmlFor="email">
                  Email address
                </label>
                <input
                  {...register("email", {
                    required: "Required field",
                    pattern: {
                      value: emailRegex,
                      message: "Please provide a valid email address"
                    }
                  })}
                  type="text"
                  id="email"
                  className="w-full p-3 border rounded-lg bg-transparent focus:outline-none"
                  style={{ borderColor: errors.email ? "#ef4444" : "#ed7f3d" }}
                />

                <label className="ml-1 mb-2 mt-4" htmlFor="password">
                  Password
                </label>
                <div className="relative">
                  <input
                    {...register("password", { required: "Required field" })}
                    type={showPassword ? "text" : "password"}
                    id="password"
                    className="w-full p-3 pr-10 border rounded-lg bg-transparent focus:outline-none"
                    style={{ borderColor: errors.password ? "#ef4444" : "#ed7f3d" }}
                  />
                  <button
                    type="button"
                    onClick={toggleShowPassword}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <SVG name={showPassword ? "EyeClosed" : "EyeOpen"} />
                  </button>
                </div>

                <div className="flex items-start gap-3 mt-4 ml-1">
                  <Controller
                    control={control}
                    name="tos"
                    render={({ field: { value, onChange } }) => (
                      <div className="flex-shrink-0 mt-0.5">
                        <Checkbox checked={value} onChange={onChange} id="tos" />
                      </div>
                    )}
                  />
                  <Typography htmlFor="tos" variant="subtitle2" as="label" className="text-sm flex-1 leading-relaxed">
                    I accept the Mashoorah{" "}
                    <a className="text-skin-accent" href="https://next.mashoorah.co/en/terms" target="_blank" rel="noreferrer">
                      Terms & Conditions
                    </a>{" "}
                    and have read the{" "}
                    <a className="text-skin-accent" href="https://next.mashoorah.co/en/privacy" target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>
                    .
                  </Typography>
                </div>

                {errorMessage && (
                  <Typography
                    as={Stack}
                    alignItems="center"
                    gap={2}
                    fontWeight="semibold"
                    justifyContent="center"
                    variant="body2"
                    className="mt-4"
                    color="warning"
                  >
                    <SVG name="Danger" />
                    <span>{errorMessage}</span>
                  </Typography>
                )}

                <Link to="/auth/forgot-password" className="flex items-start cursor-pointer ml-1 italic mt-2">
                  Forgot password?
                </Link>

                <button
                  type="submit"
                  disabled={isLoading || !watch("tos")}
                  className="py-3 font-semibold mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: colorOrange,
                    width: "fit-content",
                    color: "white",
                    padding: "0.6rem 2rem",
                    alignSelf: "end",
                    borderRadius: "9999px",
                    transition: "background-color 0.3s ease",
                    fontWeight: 600
                  }}
                >
                  {isLoading ? "Loading..." : "Login"}
                </button>
              </motion.form>
              <Link to="/" className="flex items-start w-64 md:w-96 mt-20">
                <img src={MashoorahLogo} alt="Mashoorah Logo" className="h-16 md:h-20 w-auto" />
              </Link>
            </div>

            <div 
              className="flex-1 bg-cover bg-center hidden lg:block" 
              style={{ backgroundImage: `url(${loginPageImg})` }}
            />
          </div>
        )}
        {showOtp && (
          <div className="flex w-full h-full" key="otp">
            <div className="flex-1 flex items-center justify-center bg-gray-100 flex-col relative">
              <OTPSection setShowOtp={setShowOtp} onSubmit={onSubmit as any} email={email} password={password} />
            </div>

            <div 
              className="flex-1 bg-cover bg-center hidden lg:block" 
              style={{ backgroundImage: `url(${loginPageImg})` }}
            />
          </div>
        )}
      </AnimatePresence>
      <BaseDialog dialogClassName="max-w-lg" onClose={() => setShowBlockedModal(false)} isOpen={showBlockedModal}>
        <AccountLockDialog />
      </BaseDialog>
    </div>
  );
};

export default LoginPage;